import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { useVendorRemovalPreview } from '@/hooks/useVendors'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { FormError } from '@/components/ui/form-error'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatEventDate } from '@/lib/eventTime'
import type { VendorRemovalTarget } from '@/services/VendorService'
import type { VendorRemovalPreviewDto } from '@lib/dto/vendor.dto'

/** Names, as a sentence: "Pat", "Pat and Sam", "Pat, Sam and Alex". */
function names(list: readonly string[]): string {
  if (list.length <= 1) return list.join('')
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1] ?? ''}`
}

/**
 * Who hears of it: the company once, listing its events; the couples whose
 * planner it was or whose plans had its furniture; and those plans' planners.
 */
function toldLine(preview: VendorRemovalPreviewDto): string {
  const company = preview.told.filter((t) => t.as === 'company').map((t) => t.name)
  const couples = preview.told.filter((t) => t.as === 'couple').map((t) => t.name)
  const planners = preview.told.filter((t) => t.as === 'planner').map((t) => t.name)
  const parts = [
    company.length > 0 ? `${names(company)} at ${preview.vendorName}, once, with the list` : null,
    couples.length > 0 ? `the ${couples.length === 1 ? 'couple' : 'couples'}, ${names(couples)}` : null,
    planners.length > 0 ? `the ${planners.length === 1 ? 'planner' : 'planners'}, ${names(planners)}` : null,
  ].filter((part) => part !== null)
  if (parts.length === 0) return 'Nobody is emailed about it.'
  return `Emailed: ${parts.join('; and ')}.`
}

type RemovedPiece = VendorRemovalPreviewDto['rentals'][number]['pieces'][number]

/** One piece and where it is: "Farm table ×6, on Reception and Dinner (confirmed, kept)". */
function pieceLine(piece: RemovedPiece): string {
  const count = `${piece.itemName} ×${String(piece.quantity)}`
  if (piece.plans.length === 0) return `${count}, on no plan yet`
  const plans = piece.plans.map((p) => (p.settled ? `${p.phaseLabel} (confirmed, kept)` : p.phaseLabel))
  return `${count}, on ${names(plans)}`
}

/** The upcoming events renting the company's furniture, each piece that comes off, and where it is. */
function RentalsBody({ preview, showVenue }: { preview: VendorRemovalPreviewDto; showVenue: boolean }): React.ReactElement | null {
  const count = preview.rentals.length
  if (count === 0) return null
  return (
    <>
      <p>
        Its furniture comes off {count === 1 ? 'this upcoming event' : `these ${String(count)} upcoming events`}:
      </p>
      <ul className="max-h-48 list-disc space-y-1 overflow-y-auto pl-5">
        {preview.rentals.map((r) => (
          <li key={r.eventId}>
            <span className="font-medium text-ink">{r.eventName}</span>
            {' — '}
            {r.eventDate ? formatEventDate(r.eventDate) : 'no date yet'}
            {showVenue ? `, ${r.venueName}` : ''}
            <ul className="list-[circle] pl-4">
              {r.pieces.map((piece, i) => (
                <li key={`${piece.itemName}-${String(i)}`}>{pieceLine(piece)}</li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      <p>
        On an open plan its pieces stay, marked as removed, until someone takes them off. A confirmed plan keeps them as
        it was confirmed.
      </p>
    </>
  )
}

/** The upcoming events the company is assigned to, which it comes off. */
function AssignmentsBody({ preview, showVenue }: { preview: VendorRemovalPreviewDto; showVenue: boolean }): React.ReactElement | null {
  const count = preview.events.length
  if (count === 0) return null
  return (
    <>
      <p>
        {preview.vendorName} comes off {count === 1 ? 'this upcoming event' : `these ${String(count)} upcoming events`},
        and its people lose {count === 1 ? 'it' : 'them'} at once:
      </p>
      <ul className="max-h-48 list-disc space-y-0.5 overflow-y-auto pl-5">
        {preview.events.map((e) => (
          <li key={e.eventId}>
            <span className="font-medium text-ink">{e.eventName}</span>
            {' — '}
            {e.eventDate ? formatEventDate(e.eventDate) : 'no date yet'}
            {showVenue ? `, ${e.venueName}` : ''}
          </li>
        ))}
      </ul>
    </>
  )
}

/** The warning's body: the events the company and its furniture come off, what stays, and who is told. */
function PreviewBody({
  preview,
  lead,
  showVenue,
}: {
  preview: VendorRemovalPreviewDto
  lead: string
  showVenue: boolean
}): React.ReactElement {
  const onNothing = preview.events.length === 0 && preview.rentals.length === 0
  return (
    <div className="space-y-3">
      <p>{lead}</p>
      {onNothing ? <p>{preview.vendorName} is on no upcoming events.</p> : null}
      <AssignmentsBody preview={preview} showVenue={showVenue} />
      <RentalsBody preview={preview} showVenue={showVenue} />
      <p>Past and canceled events keep it on record.</p>
      <p>{toldLine(preview)}</p>
    </div>
  )
}

/**
 * The warning before a company stops work — at a venue, or everywhere when it
 * is deactivated (docs/planner-vendors.md, decisions 36 and 39). The preview is
 * asked for each time it opens, and read the same way as the change, so what it
 * lists is what the confirmation does.
 */
export function VendorRemovalDialog({
  open,
  onOpenChange,
  target,
  title,
  lead,
  confirmLabel,
  busy,
  error,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  target: VendorRemovalTarget
  title: string
  /** What the action itself does, before the events it touches. */
  lead: string
  confirmLabel: string
  busy: boolean
  error: string | null
  onConfirm: () => void
}): React.ReactElement {
  const preview = useVendorRemovalPreview(target, open)

  function body(): React.ReactNode {
    if (preview.isPending) {
      return (
        <span className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking its events…
        </span>
      )
    }
    if (preview.isError) return requestErrorMessage(preview.error)
    return <PreviewBody preview={preview.data} lead={lead} showVenue={target.kind === 'deactivate'} />
  }

  // Built on the dialog rather than ConfirmDialog, whose description is a
  // paragraph: this one holds a list.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div>{body()}</div>
          </DialogDescription>
        </DialogHeader>
        {error ? <FormError>{error}</FormError> : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {/* Not until the warning has loaded: confirming what nobody was shown is not confirming. */}
          <Button pending={busy} type="button" variant="destructive" disabled={!preview.isSuccess} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
